import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useNews } from "@/hooks/content";
import { PageHeader, Field, useToast, newId } from "../components/admin-ui";
import {
  newsCategories,
  newsStatusOptions,
  type AdminArticle,
  type NewsStatus,
} from "@/mocks/newsAdmin";

const emptyForm = {
  headline: "",
  subtitle: "",
  excerpt: "",
  content: "",
  category: "",
  author: "",
  location: "Edea, Cameroon",
  tags: [] as string[],
  image: "",
  status: "draft" as NewsStatus,
  featured: false,
  breaking: false,
  seoTitle: "",
  metaDescription: "",
};

export default function ArticleEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { show } = useToast();
  const { items, add, update } = useNews();

  const existing = id ? items.find((a) => a.id === id) : undefined;

  const [form, setForm] = useState<typeof emptyForm>(() => {
    if (existing) {
      return {
        headline: existing.headline,
        subtitle: existing.subtitle,
        excerpt: existing.excerpt,
        content: existing.content,
        category: existing.category,
        author: existing.author,
        location: existing.location,
        tags: existing.tags,
        image: existing.image,
        status: existing.status,
        featured: existing.featured,
        breaking: existing.breaking,
        seoTitle: existing.seoTitle,
        metaDescription: existing.metaDescription,
      };
    }
    return emptyForm;
  });

  function set<K extends keyof typeof emptyForm>(key: K, value: (typeof emptyForm)[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function slugify(text: string) {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "")
      .slice(0, 80);
  }

  function save(statusOverride?: NewsStatus) {
    if (!form.headline.trim()) {
      show("Headline is required.", "error");
      return;
    }
    if (!form.category) {
      show("Please select a category.", "error");
      return;
    }
    const finalStatus = statusOverride ?? form.status;

    if (existing) {
      update(existing.id, {
        ...form,
        status: finalStatus,
        slug: slugify(form.headline) || existing.slug,
      });
      show("Article saved.");
    } else {
      const readTime = `${Math.max(1, Math.round(form.content.split(" ").length / 200))} min read`;
      const article: AdminArticle = {
        id: newId(),
        slug: slugify(form.headline),
        headline: form.headline,
        subtitle: form.subtitle,
        excerpt: form.excerpt || form.content.slice(0, 140),
        content: form.content,
        category: form.category,
        image: form.image,
        author: form.author || "L+TV News Desk",
        authorImage: "",
        publishedAt: new Date().toISOString(),
        readTime,
        featured: form.featured,
        breaking: form.breaking,
        tags: form.tags,
        status: finalStatus,
        location: form.location,
        seoTitle: form.seoTitle || form.headline,
        metaDescription: form.metaDescription || form.excerpt,
      };
      add(article);
      show("Article created.");
    }
    navigate("/admin/news");
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={existing ? "Edit Article" : "New Article"}
        subtitle="Manage article content and editorial status."
        actions={
          <button
            type="button"
            onClick={() => navigate("/admin/news")}
            className="px-4 py-2 text-sm font-medium text-foreground-700 border border-background-200 rounded-md hover:bg-background-50 whitespace-nowrap"
          >
            Cancel
          </button>
        }
      />

      <div className="bg-white rounded-lg border border-background-200 p-5">
        <div className="mb-5">
          <p className="text-sm font-semibold text-foreground-900 mb-2">Editorial status</p>
          <div className="flex flex-wrap gap-2">
            {newsStatusOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => set("status", opt.value)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border whitespace-nowrap transition-colors ${
                  form.status === opt.value
                    ? "bg-primary-600 text-white border-primary-600"
                    : "bg-white text-foreground-700 border-background-200 hover:border-primary-300"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="col-span-2">
            <Field
              def={{ name: "headline", label: "Headline", type: "text", required: true, full: true }}
              value={form.headline}
              onChange={(v) => set("headline", String(v))}
            />
          </div>
          <div className="col-span-2">
            <Field
              def={{ name: "subtitle", label: "Subtitle", type: "text", full: true }}
              value={form.subtitle}
              onChange={(v) => set("subtitle", String(v))}
            />
          </div>
          <div className="col-span-2">
            <Field
              def={{ name: "category", label: "Category", type: "select", required: true, options: newsCategories.map((c) => ({ value: c, label: c })), full: true }}
              value={form.category}
              onChange={(v) => set("category", String(v))}
            />
          </div>
          <Field
            def={{ name: "author", label: "Author", type: "text" }}
            value={form.author}
            onChange={(v) => set("author", String(v))}
          />
          <Field
            def={{ name: "location", label: "Location", type: "text" }}
            value={form.location}
            onChange={(v) => set("location", String(v))}
          />
          <div className="col-span-2">
            <Field
              def={{ name: "excerpt", label: "Excerpt", type: "textarea", full: true }}
              value={form.excerpt}
              onChange={(v) => set("excerpt", String(v))}
            />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-foreground-800 mb-1.5">Article content</label>
            <textarea
              value={form.content}
              onChange={(e) => set("content", e.target.value)}
              rows={12}
              placeholder="Write the full article body…"
              className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
          </div>
          <div className="col-span-2">
            <Field
              def={{ name: "image", label: "Featured image URL", type: "text", full: true, help: "Leave blank to use a placeholder." }}
              value={form.image}
              onChange={(v) => set("image", String(v))}
            />
          </div>
          <div className="col-span-2">
            <Field
              def={{ name: "tags", label: "Tags", type: "tags", full: true }}
              value={form.tags}
              onChange={(v) => set("tags", v as string[])}
            />
          </div>
          <Field
            def={{ name: "featured", label: "Featured article", type: "toggle" }}
            value={form.featured}
            onChange={(v) => set("featured", Boolean(v))}
          />
          <Field
            def={{ name: "breaking", label: "Breaking news", type: "toggle" }}
            value={form.breaking}
            onChange={(v) => set("breaking", Boolean(v))}
          />

          <div className="col-span-2 border-t border-background-100 pt-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-foreground-500 mb-3">
              SEO metadata
            </p>
          </div>
          <div className="col-span-2">
            <Field
              def={{ name: "seoTitle", label: "SEO title", type: "text", full: true }}
              value={form.seoTitle}
              onChange={(v) => set("seoTitle", String(v))}
            />
          </div>
          <div className="col-span-2">
            <Field
              def={{ name: "metaDescription", label: "Meta description", type: "textarea", full: true }}
              value={form.metaDescription}
              onChange={(v) => set("metaDescription", String(v))}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={save}
          className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 whitespace-nowrap"
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-save-line text-sm" />
          </span>
          Save Article
        </button>
        {form.status !== "published" && (
          <button
            type="button"
            onClick={() => save("published")}
            className="px-5 py-2.5 text-sm font-medium text-green-700 border border-green-200 rounded-md hover:bg-green-50 whitespace-nowrap"
          >
            Save & Publish
          </button>
        )}
      </div>
    </div>
  );
}